import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "./store";
import { useLazyGetAllSkillsQuery } from "../services/skills";
import {
  setSkills,
  addSkill,
  updateSkill,
  removeSkill,
  selectSkillsList,
  selectIsSkillsCacheValid,
  SkillListItem,
} from "../reducers/skillsSlice";
import { getErrorMessage } from "../types/api";

interface UseSkillsOptions {
  /** If true, fetches data on mount. Default: true */
  fetchOnMount?: boolean;
}

interface UseSkillsReturn {
  /** List of skills from Redux store */
  skills: SkillListItem[];
  /** Whether data is currently being fetched */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Whether the cache is still valid (< 10 min old) */
  isCacheValid: boolean;
  /** Fetch skills - uses cache if valid, otherwise fetches from API */
  fetchSkills: () => Promise<void>;
  /** Force refetch from API, ignoring cache */
  refetch: () => Promise<void>;
  /** Add a skill to Redux store */
  addSkillToStore: (skill: SkillListItem) => void;
  /** Update a skill in Redux store */
  updateSkillInStore: (id: string, data: Partial<SkillListItem>) => void;
  /** Remove a skill from Redux store */
  removeSkillFromStore: (id: string) => void;
}

/**
 * Hook to manage skills data with Redux caching.
 * Automatically uses cached data if it's less than 10 minutes old.
 */
const useSkills = (options: UseSkillsOptions = {}): UseSkillsReturn => {
  const { fetchOnMount = true } = options;

  const dispatch = useAppDispatch();
  const skills = useAppSelector(selectSkillsList);
  const isCacheValid = useAppSelector(selectIsSkillsCacheValid);

  const [getAllSkillsApi] = useLazyGetAllSkillsQuery();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetches skills from API and updates Redux store
   */
  const fetchFromApi = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getAllSkillsApi().unwrap();
      if (response?.statusCode === 200) {
        const allData =
          response?.data?.data?.map((data: { _id: string }) => ({
            ...data,
            id: data?._id,
          })) || [];
        dispatch(
          setSkills({
            list: allData,
            lastFetchedAt: Date.now(),
          })
        );
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to load skills"));
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, getAllSkillsApi]);

  /**
   * Fetch skills - uses cache if valid, otherwise fetches from API
   */
  const fetchSkills = useCallback(async () => {
    if (isCacheValid && skills.length > 0) {
      return;
    }
    await fetchFromApi();
  }, [isCacheValid, skills.length, fetchFromApi]);

  /**
   * Force refetch from API, ignoring cache
   */
  const refetch = useCallback(async () => {
    await fetchFromApi();
  }, [fetchFromApi]);

  /**
   * Add a skill to Redux store
   */
  const addSkillToStore = useCallback(
    (skill: SkillListItem) => {
      dispatch(addSkill(skill));
    },
    [dispatch]
  );

  /**
   * Update a skill in Redux store
   */
  const updateSkillInStore = useCallback(
    (id: string, data: Partial<SkillListItem>) => {
      dispatch(updateSkill({ id, data }));
    },
    [dispatch]
  );

  /**
   * Remove a skill from Redux store
   */
  const removeSkillFromStore = useCallback(
    (id: string) => {
      dispatch(removeSkill(id));
    },
    [dispatch]
  );

  // Fetch on mount if enabled
  useEffect(() => {
    if (fetchOnMount) {
      fetchSkills();
    }
  }, []);

  return {
    skills,
    isLoading,
    error,
    isCacheValid,
    fetchSkills,
    refetch,
    addSkillToStore,
    updateSkillInStore,
    removeSkillFromStore,
  };
};

export default useSkills;
